import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { CoursesByNameItem } from './CoursesByNameItem';
import { FlexBox } from '../Layout/Flex';
import dynamic from 'next/dynamic';
import Cross from '../../public/images/cross_mobile.svg';
import { useMediaQuery } from 'react-responsive';
import { Consts as StyleConsts } from '../../styles/consts';

interface ICoursesByName {
  title: string;
  data?: {
    content: {
      body: [] | any;
      preview_title: string;
      preview_image: {
        filename: string;
        alt: string;
      };
      percentComplete?: string;
      category: string;
      description?: string;
      video_description?: string;
    };
    id: number;
    uuid: string;
    full_slug: string;
  }[];
  lessonsWithProgress?: {
    lessonId: number;
    completionPercents: number;
  }[];
  learningContent?: boolean;
  zIndex?: number;
}

const carouselSettings = {
  infinite: false,
  slidesToShow: 4,
  slidesToScroll: 1,
  autoplay: false,
  autoplaySpeed: 3000,
  pauseOnHover: false,
  dots: false,
  variableWidth: false,
  arrows: true,
  initialSlide: 0,
  swipeToSlide: true,
  swipe: true,

  responsive: [
    {
      breakpoint: 1380,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
        infinite: false,
        dots: false,
      },
    },
    {
      breakpoint: 910,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
        infinite: false,
        dots: true,
      },
    },
    {
      breakpoint: 630,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        infinite: false,
        dots: true,
      },
    },
  ],
};
export const Video = dynamic(() => import('../LessonContent/Video'), {
  ssr: false,
});
export const CoursesByName: FunctionComponent<ICoursesByName> = ({
  title,
  data,
  lessonsWithProgress,
  learningContent,
  zIndex,
}) => {
  const [isShow, setShow] = useState(false);
  const [modalFullSlug, setFullSlug] = useState();
  const [modalTitle, setTitle] = useState();
  const [modalDescription, setDescription] = useState();
  const [modalVideo, setVideo] = useState();
  const [height, setHeight] = useState(0);

  const closePopUp = () => {
    setShow(false);
    setFullSlug(null);
    setTitle(null);
    setDescription(null);
    setVideo(null);

    const layout = document.getElementById('layout') as HTMLDivElement;
    layout.style.overflowY = 'auto';
    layout.style.height = 'initial';
    layout.style.maxHeight = 'initial';

    window.scrollTo({ top: height, behavior: 'auto' });
    (
      document.getElementsByTagName('HTML') as HTMLCollectionOf<HTMLElement>
    )[0].style.scrollBehavior = 'smooth';
  };
  const [isMobile, setMobile] = useState(false);
  const mobile = useMediaQuery({
    query: `(max-width: ${StyleConsts.mobileBreakPoint})`,
  });

  useEffect(() => {
    setMobile(mobile);
  }, [mobile]);

  return (
    <>
      {isShow && isMobile ? (
        <Overlay>
          <WrapperCrossBtn>
            <Cross onClick={closePopUp} />
          </WrapperCrossBtn>
          <Video
            blok={modalVideo}
            learningContent={learningContent}
            link={modalFullSlug}
            isShow={isShow}
          />
          <TextWrapper>
            <OverlayTitle>{modalTitle}</OverlayTitle>
            <OverlayText>{modalDescription}</OverlayText>
          </TextWrapper>
        </Overlay>
      ) : (
        <CoursesByNameWrapper style={{ zIndex: zIndex }}>
          <Title>{title}</Title>
          <SlidersFlexWrapper learningContent={learningContent}>
            <Slider {...carouselSettings}>
              {data.map(({ content, uuid, full_slug: fullSlug, id }, i) => (
                <CoursesByNameItem
                  setShow={setShow}
                  setFullSlug={setFullSlug}
                  setTitle={setTitle}
                  setDescription={setDescription}
                  setVideo={setVideo}
                  setHeight={setHeight}
                  learningContent={learningContent}
                  key={uuid}
                  previewTitle={content.preview_title}
                  previewImage={content.preview_image}
                  fullSlug={fullSlug}
                  description={content.video_description}
                  video={
                    content.body.filter(
                      (el) => el.component === 'main_video',
                    )[0].small_video[0]
                  }
                  percentComplete={
                    lessonsWithProgress
                      ?.filter((el) => el.lessonId === id)
                      .map((el) => el.completionPercents)[0]
                  }
                />
              ))}
            </Slider>
          </SlidersFlexWrapper>
        </CoursesByNameWrapper>
      )}
    </>
  );
};

const CoursesByNameWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: flex;
    flex-direction: column;
  `}
`;
const Title = styled.h2`
  ${({ theme: { Consts } }) => css`
    font-weight: 800;
    font-size: 30px;
    line-height: 42px;
    color: #ffffff;
    padding: 0 0 16px 28px;
    @media (max-width: ${Consts.mobileBreakPoint}) {
      padding: 0 0 20px;
    }
  `}
`;

interface ISlidersFlexWrapper {
  learningContent?: boolean;
}

const SlidersFlexWrapper = styled(FlexBox)<ISlidersFlexWrapper>`
  ${({ learningContent, theme: { Consts } }) => css`
    width: 100%;
    padding: 0 20px;

    .slick-list {
      overflow: visible;

      .slick-slide {
        opacity: 0;
        pointer-events: none;
      }

      .slick-active {
        opacity: 1 !important;
        pointer-events: all;
      }
    }

    .slick-track {
      margin-left: 0;
    }

    .slick-prev,
    .slick-next {
      display: flex !important;
    }

    .slick-prev {
      left: ${learningContent ? '-20px' : '-30px'};
      top: calc(50% - 23px);
      justify-content: flex-start;
      z-index: 5;
    }

    .slick-prev:before {
      content: url('/images/arrow-next.svg');
    }

    .slick-next {
      right: ${learningContent ? '-20px' : '-30px'};
      top: calc(50% - 23px);
      justify-content: flex-end;
    }

    .slick-next:before {
      content: url('/images/arrow-prev.svg');
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      padding: 0 30px;
      .slick-track {
        margin-left: auto;
      }

      .slick-prev {
        left: -38px;
        top: calc(50% - 35.5px);
        @media (max-width: ${Consts.mobileBreakPoint}) {
          left: -30px;
        }
      }

      .slick-next {
        right: -38px;
        top: calc(50% - 35.5px);
        @media (max-width: ${Consts.mobileBreakPoint}) {
          right: -30px;
        }
      }
    }

    .slick-slider {
      .slick-dots {
        position: static;

        > li {
          width: 12px;
          height: 12px;
          margin: 0 4px;
          padding: 0;

          > button {
            padding: 0 4px;
            width: 12px;
            height: 12px;

            &::before {
              color: #d9d9d9 !important;
              opacity: 1;
              width: 12px;
              height: 12px;
            }
          }

          &.slick-active {
            > button {
              &::before {
                color: #45b0a6 !important;
                opacity: 1;
              }
            }
          }
        }
      }
    }
  `}
`;
const Overlay = styled.div`
  ${({ theme: { Consts } }) => css`
    display: flex;
    flex-direction: column;
    position: absolute;
    width: 100vw;
    height: 100vh;
    z-index: 45;
    background: #222222;
    opacity: 1;
    top: 0;
    left: 0;
    overflow-y: hidden !important;
    padding-bottom: 20px;
  `}
`;
const WrapperCrossBtn = styled.div`
  ${({ theme: { Consts } }) => css`
    position: absolute;
    top: 4px;
    right: 16px;
    cursor: pointer;
    width: 16px;
    height: 16px;
    display: flex;
    z-index: 2;
  `}
`;
const TextWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-top: 20px;
    overflow-y: overlay;
  `}
`;
const OverlayTitle = styled.h4`
  ${({ theme: { Consts } }) => css`
    font-weight: 700;
    font-size: 17px;
    line-height: 22px;
    letter-spacing: 0.01em;
    padding: 0 20px 20px;
    border-bottom: 0.5px solid rgba(255, 255, 255, 0.1);
  `}
`;
const OverlayText = styled.p`
  ${({ theme: { Consts } }) => css`
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0.01em;
    color: #ebf5f3;
    padding-right: 12px;
    overflow-y: scroll;
    margin: 0 20px;

    &::-webkit-scrollbar {
      width: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background: #ebf5f3;
      height: 35px;
    }
  `}
`;
